import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot, ArrowRight, Settings2 } from 'lucide-react';
import { useAgentKey } from '../../hooks/useAgentKey';
import agentsService, { Agent } from '../../services/agentsService';
import RequireAgent from '../../components/RequireAgent';
import { PageHeader, Spinner } from '../../components/ui';
import MarketingWorkspace from './MarketingWorkspace';
import JiraWorkspace from './JiraWorkspace';
import '../../styles/theme.css';

/**
 * Generic, agent-scoped hub reached at /admin/agent/:agentKey. The Doc agent's full workspace is
 * the existing Documents view (/admin/docshawk); other agents (marketing / jira, later phases)
 * render here on the SAME shared engine once their flow is registered. Access is gated by
 * RequireAgent so a deep-link the user can't use shows a clean no-access state.
 */
const AgentWorkspaceInner: React.FC<{ agentKey: string }> = ({ agentKey }) => {
  const [agent, setAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    agentsService.getAgent(agentKey)
      .then(({ agent }) => alive && setAgent(agent))
      .catch(() => alive && setAgent(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [agentKey]);

  if (loading) return <div className="mx-card mx-card-pad"><Spinner label="Loading agent…" /></div>;
  if (!agent) return <div className="mx-card mx-card-pad mx-muted">Agent “{agentKey}” not found.</div>;

  // The marketing agent has a full brief → live pipeline → review workspace on the shared engine.
  if (agent.key === 'marketing') return <MarketingWorkspace agent={agent} />;
  // The IT / Jira agent: connect → pull tickets → triage → review → apply back to Jira.
  if (agent.key === 'jira') return <JiraWorkspace agent={agent} />;

  return (
    <div>
      <PageHeader title={agent.name} subtitle={agent.persona || agent.description} />
      <div className="mx-card mx-card-pad" style={{ maxWidth: 640 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, color: 'var(--mx-accent)' }}>
          <Bot size={18} /><span style={{ fontWeight: 600, color: 'var(--mx-text)' }}>{agent.name}</span>
        </div>
        {agent.description && (
          <div className="mx-muted" style={{ fontSize: 13, marginTop: 8 }}>{agent.description}</div>
        )}
        <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {agent.key === 'doc' ? (
            <Link to="/admin/docshawk" className="mx-card mx-card-pad" style={{ textDecoration: 'none', flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--mx-accent)' }}>
                Open documents <ArrowRight size={14} />
              </div>
              <div className="mx-muted" style={{ fontSize: 12.5, marginTop: 5 }}>Upload and review this agent’s documents.</div>
            </Link>
          ) : (
            <div className="mx-card mx-card-pad" style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 600 }}>Pipeline shares the shared engine</div>
              <div className="mx-muted" style={{ fontSize: 12.5, marginTop: 5 }}>
                This agent runs on the same orchestrator, live event bus, and eval-gate. Its flow
                ships in a later phase.
              </div>
            </div>
          )}
          {agent.can_configure && (
            <Link to={`/admin/agents/${agent.key}/config`} className="mx-card mx-card-pad" style={{ textDecoration: 'none', flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--mx-accent)' }}>
                <Settings2 size={15} /> Configure agent
              </div>
              <div className="mx-muted" style={{ fontSize: 12.5, marginTop: 5 }}>Base prompt, params, and guardrails.</div>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

const AgentWorkspace: React.FC = () => {
  const agentKey = useAgentKey();
  return (
    <RequireAgent agentKey={agentKey}>
      <AgentWorkspaceInner agentKey={agentKey} />
    </RequireAgent>
  );
};

export default AgentWorkspace;
